import { Box } from "@mui/material";
import { useId } from "react";
import { brainTokens } from "../design/brainTheme";

const c = brainTokens.color;

/**
 * Inline SVG area chart matching the design (teal line + gradient fill + mono
 * axis footer). recharts isn't a dep; the old pages hand-roll SVG too. Used by
 * Overview (knowledge growth) and Finance (AWS cost, data growth).
 */
export function AreaChart({
  values,
  leftLabel,
  rightLabel,
  height = 160,
}: {
  values: number[];
  leftLabel?: string;
  rightLabel?: string;
  height?: number;
}) {
  const gid = useId().replace(/[:]/g, "");
  const W = 1000;
  const H = 180;
  const pad = 12;

  const pts = values.length ? values : [0, 0];
  const max = Math.max(...pts, 1);
  const min = Math.min(...pts, 0);
  const span = max - min || 1;
  const stepX = pts.length > 1 ? W / (pts.length - 1) : W;
  const y = (v: number) => H - pad - ((v - min) / span) * (H - pad * 2);

  const line = pts.map((v, i) => `${i === 0 ? "M" : "L"}${(i * stepX).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const area = `${line} L${W},${H} L0,${H} Z`;

  return (
    <Box>
      <Box component="svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" sx={{ width: "100%", height, display: "block" }}>
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={c.tealInk2} stopOpacity="0.16" />
            <stop offset="1" stopColor={c.tealInk2} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#${gid})`} />
        <path d={line} fill="none" stroke={c.tealInk2} strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
      </Box>
      {(leftLabel || rightLabel) && (
        <Box sx={{ display: "flex", justifyContent: "space-between", fontFamily: brainTokens.font.mono, fontSize: 11, color: "#b0b6bc", mt: "8px" }}>
          <span>{leftLabel}</span>
          <span>{rightLabel}</span>
        </Box>
      )}
    </Box>
  );
}
